import React, { useState } from 'react';
import { Megaphone, Loader2, CheckCircle } from 'lucide-react';
import MenuPageShell from '../MenuPageShell';

export default function EnquiryPage({ lang, onBack }) {
  const [form, setForm] = useState({ name: '', company: '', phone: '', email: '', queryType: 'ads', message: '' });
  const [errs, setErrs] = useState({});
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiErr, setApiErr] = useState('');

  const L = (zh, en, ja, ko) => lang==='en'?en:lang==='ja'?(ja||en):lang==='ko'?(ko||en):zh;

  const EL = {
    title:       L('廣告查詢','Ad Enquiry','広告のお問い合わせ','광고 문의'),
    intro:       L('歡迎與 EC Travel 合作！如有廣告投放、商業合作或媒體查詢，請填寫以下表格，我們將於 2 個工作天內回覆。',
                   'Welcome to collaborate with EC Travel! For advertising, partnership, or media enquiries, fill in the form below. We\'ll respond within 2 business days.',
                   'EC Travel との協力をお待ちしております！広告、パートナーシップ、メディアのお問い合わせは以下のフォームにご記入ください。2営業日以内にご連絡いたします。',
                   'EC Travel과의 협력을 환영합니다! 광고, 파트너십, 미디어 문의는 아래 양식을 작성해 주세요. 2 영업일 내에 답변드리겠습니다.'),
    typeLabel:   L('查詢類型','Enquiry Type','お問い合わせ種別','문의 유형'),
    contact:     L('聯絡人姓名','Contact Name','担当者名','담당자 이름'),
    contactPh:   L('請輸入姓名','Enter your name','お名前を入力','이름을 입력'),
    company:     L('公司名稱','Company','会社名','회사명'),
    companyPh:   L('選填','Optional','任意','선택'),
    phone:       L('電話號碼','Phone','電話番号','전화번호'),
    phonePh:     L('選填','Optional','任意','선택'),
    email:       L('電郵地址','Email','メールアドレス','이메일'),
    message:     L('查詢內容','Message','お問い合わせ内容','문의 내용'),
    messagePh:   L('請描述您的查詢或合作需求…','Describe your enquiry or collaboration needs…','お問い合わせ内容や協力ニーズをご記入ください…','문의 내용이나 협력 요구사항을 설명해 주세요…'),
    submit:      L('提交查詢','Submit Enquiry','お問い合わせを送信','문의 제출'),
    submitting:  L('提交中…','Submitting…','送信中…','제출 중…'),
    back:        L('返回主頁','Go Back','戻る','돌아가기'),
    agree:       L('提交即表示同意 EC Travel 使用您的資料處理本次查詢',
                   'By submitting, you agree to EC Travel using your data to handle this enquiry.',
                   '送信することで、EC Travel がこのお問い合わせ処理のためにお客様のデータを使用することに同意します。',
                   '제출하면 EC Travel이 이 문의를 처리하기 위해 귀하의 데이터를 사용하는 것에 동의합니다.'),
    sentTitle:   L('查詢已成功提交！','Enquiry Submitted!','お問い合わせを受け付けました！','문의가 성공적으로 접수되었습니다!'),
    sentDesc:    L('我們的商業合作團隊將於 2 個工作天內以電郵回覆。\n如有緊急查詢，請直接聯絡',
                   'Our team will reply by email within 2 business days.\nFor urgent enquiries, please contact',
                   '担当チームが2営業日以内にメールでご連絡いたします。\n緊急のお問い合わせは直接ご連絡ください',
                   '담당 팀이 2 영업일 내에 이메일로 답변드리겠습니다.\n긴급 문의는 직접 연락 주세요'),
    errName:     L('請填寫姓名','Name is required','名前を入力してください','이름을 입력해주세요'),
    errEmail:    L('請填寫有效電郵地址','Valid email required','有効なメールアドレスを入力してください','유효한 이메일을 입력해주세요'),
    errMsg:      L('請填寫查詢內容','Message is required','内容を入力してください','문의 내용을 입력해주세요'),
  };

  const QUERY_TYPES = [
    { k: 'ads',         label: L('廣告投放','Advertising','広告掲載','광고 게재') },
    { k: 'partnership', label: L('商業合作','Partnership','ビジネス提携','비즈니스 제휴') },
    { k: 'media',       label: L('媒體查詢','Media Enquiry','メディア','미디어 문의') },
  ];

  const inp = {
    width: '100%', border: '1.5px solid #e2e8f0', borderRadius: 12,
    padding: '11px 14px', fontSize: 13, outline: 'none', background: 'white',
    boxSizing: 'border-box', color: '#0f172a', fontFamily: 'inherit',
  };
  const lbl = { fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6, display: 'block' };
  const errStyle = { ...inp, borderColor: '#ef4444', background: '#fff5f5' };

  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setErrs(p => ({ ...p, [k]: false })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = true;
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = true;
    if (!form.message.trim()) e.message = true;
    return e;
  };

  const submit = async () => {
    const e = validate();
    setErrs(e);
    if (Object.keys(e).length) return;
    setLoading(true);
    setApiErr('');
    try {
      const r = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'contact', ...form }),
      });
      if (r.ok) {
        setSent(true);
      } else {
        const d = await r.json().catch(() => ({}));
        setApiErr(d.error || L('提交失敗，請稍後重試。','Submission failed. Please try again.','送信失敗。もう一度お試しください。','제출 실패. 다시 시도해주세요.'));
      }
    } catch {
      setApiErr(L('網絡錯誤，請稍後重試。','Network error. Please try again.','ネットワークエラー。もう一度お試しください。','네트워크 오류. 다시 시도해주세요.'));
    }
    setLoading(false);
  };

  if (sent) {
    return (
      <MenuPageShell title={EL.title} icon={Megaphone} onBack={onBack}>
        <div style={{ textAlign: 'center', padding: '48px 24px' }}>
          <CheckCircle size={56} color="#22c55e" style={{ margin: '0 auto 16px' }}/>
          <p style={{ fontSize: 18, fontWeight: 900, color: '#0f172a', margin: '0 0 8px' }}>{EL.sentTitle}</p>
          <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 24px', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
            {EL.sentDesc}<br/>
            <a href="mailto:partners@travel-ec.com" style={{ color: '#f59e0b', fontWeight: 700 }}>partners@travel-ec.com</a>
          </p>
          <button onClick={onBack}
            style={{ background: 'linear-gradient(135deg,#f59e0b,#d97706)', color: '#0f172a', border: 'none', borderRadius: 12, padding: '12px 32px', fontSize: 14, fontWeight: 900, cursor: 'pointer' }}>
            {EL.back}
          </button>
        </div>
      </MenuPageShell>
    );
  }

  return (
    <MenuPageShell title={EL.title} icon={Megaphone} onBack={onBack}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

        {/* Intro */}
        <div style={{ background: 'linear-gradient(135deg,#fffbeb,#fef3c7)', border: '1.5px solid #fde68a', borderRadius: 14, padding: '14px 18px' }}>
          <p style={{ margin: 0, fontSize: 13, color: '#92400e', lineHeight: 1.7 }}>{EL.intro}</p>
        </div>

        {/* Query type */}
        <div>
          <span style={lbl}>{EL.typeLabel} <span style={{ color: '#ef4444' }}>*</span></span>
          <div style={{ display: 'flex', gap: 8 }}>
            {QUERY_TYPES.map(qt => (
              <button key={qt.k} onClick={() => set('queryType', qt.k)}
                style={{
                  flex: 1, padding: '10px 8px', borderRadius: 10, border: '1.5px solid',
                  borderColor: form.queryType === qt.k ? '#f59e0b' : '#e2e8f0',
                  background: form.queryType === qt.k ? '#fffbeb' : 'white',
                  color: form.queryType === qt.k ? '#92400e' : '#64748b',
                  fontSize: 12, fontWeight: 700, cursor: 'pointer',
                }}>
                {qt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Contact name */}
        <div>
          <span style={lbl}>{EL.contact} <span style={{ color: '#ef4444' }}>*</span></span>
          <input style={errs.name ? errStyle : inp} placeholder={EL.contactPh} value={form.name}
            onChange={e => set('name', e.target.value)}/>
          {errs.name && <p style={{ fontSize: 11, color: '#ef4444', margin: '4px 0 0' }}>{EL.errName}</p>}
        </div>

        {/* Company */}
        <div>
          <span style={lbl}>{EL.company}</span>
          <input style={inp} placeholder={EL.companyPh} value={form.company}
            onChange={e => set('company', e.target.value)}/>
        </div>

        {/* Email */}
        <div>
          <span style={lbl}>{EL.email} <span style={{ color: '#ef4444' }}>*</span></span>
          <input style={errs.email ? errStyle : inp} placeholder="your@email.com" type="email" value={form.email}
            onChange={e => set('email', e.target.value)}/>
          {errs.email && <p style={{ fontSize: 11, color: '#ef4444', margin: '4px 0 0' }}>{EL.errEmail}</p>}
        </div>

        {/* Phone */}
        <div>
          <span style={lbl}>{EL.phone}</span>
          <input style={inp} placeholder={EL.phonePh} type="tel" value={form.phone}
            onChange={e => set('phone', e.target.value)}/>
        </div>

        {/* Message */}
        <div>
          <span style={lbl}>{EL.message} <span style={{ color: '#ef4444' }}>*</span></span>
          <textarea style={{ ...(errs.message ? errStyle : inp), resize: 'vertical', minHeight: 120, lineHeight: 1.6 }}
            placeholder={EL.messagePh}
            value={form.message} onChange={e => set('message', e.target.value)}/>
          {errs.message && <p style={{ fontSize: 11, color: '#ef4444', margin: '4px 0 0' }}>{EL.errMsg}</p>}
        </div>

        {apiErr && (
          <p style={{ fontSize: 13, color: '#ef4444', background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 10, padding: '10px 14px', margin: 0 }}>{apiErr}</p>
        )}

        <button onClick={submit} disabled={loading}
          style={{
            background: loading ? '#e2e8f0' : 'linear-gradient(135deg,#f59e0b,#d97706)',
            color: loading ? '#94a3b8' : '#0f172a',
            border: 'none', borderRadius: 14, padding: '14px', fontSize: 15, fontWeight: 900,
            cursor: loading ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
          {loading ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }}/> {EL.submitting}</> : EL.submit}
        </button>

        <p style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', margin: 0 }}>{EL.agree}</p>
      </div>
    </MenuPageShell>
  );
}
